import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Zap } from "lucide-react";
import { fmtINR } from "@/lib/format";
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";

export default function ValuationRecon() {
  const [d, setD] = useState(null);
  const [trend, setTrend] = useState([]);
  const [posting, setPosting] = useState(false);
  const load = () => {
    api.get("/reports/valuation-reconciliation").then((r) => setD(r.data)).catch(() => setD(null));
    api.get("/reports/drift-trend").then((r) => setTrend(r.data?.snapshots || [])).catch(() => setTrend([]));
  };
  useEffect(() => { load(); }, []);

  const autoCorrect = async () => {
    setPosting(true);
    try {
      const r = await api.post("/reports/valuation-recon/auto-correct", { note: "manual_period_close" });
      if (r.data.skipped) toast.success("No material drift · nothing to post");
      else toast.success(`Drift adjustment JV posted · ₹${Math.abs(r.data.drift).toFixed(2)} on ${r.data.voucher_date}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setPosting(false);
  };
  if (!d) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="valuation-recon">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase text-muted-foreground">// Valuation reconciliation · method = {d.valuation_method}</div>
        {Math.abs(d.drift || 0) > 0.5 && (
          <Button size="sm" onClick={autoCorrect} disabled={posting} className="rounded-sm gap-1 bg-red-600 hover:bg-red-700 text-white" data-testid="vr-auto-correct">
            <Zap className="w-3.5 h-3.5" /> {posting ? "Posting…" : "Auto-correct drift JV"}
          </Button>
        )}
      </div>
      <div className="grid md:grid-cols-4 gap-3">
        <Card className="rounded-sm p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">GL Inventory</div><div className="text-2xl font-bold tabular-nums mt-1">{fmtINR(d.gl_balance)}</div></Card>
        <Card className="rounded-sm p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Stock ledger</div><div className="text-2xl font-bold tabular-nums mt-1">{fmtINR(d.stock_total)}</div></Card>
        <Card className={`rounded-sm p-3 border ${Math.abs(d.drift) > 0.5 ? "border-red-500/40 bg-red-500/10" : "border-emerald-500/40 bg-emerald-500/10"}`}>
          <div className="text-[10px] font-mono uppercase">Drift</div>
          <div className={`text-2xl font-bold tabular-nums mt-1 ${Math.abs(d.drift) > 0.5 ? "text-red-800" : "text-emerald-800"}`}>{fmtINR(d.drift)}</div>
        </Card>
        <Card className="rounded-sm p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Items flagged</div><div className="text-2xl font-bold tabular-nums mt-1">{d.flagged_count}</div></Card>
      </div>

      {/* Iter-103 · Drift trend chart */}
      {trend.length > 0 && (
        <Card className="rounded-sm p-4" data-testid="drift-trend-chart">
          <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">// Drift trend · last {trend.length} snapshots</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trend.map((s, i) => ({ n: i + 1, drift: s.drift, date: s.snapshot_at?.slice(0, 10) }))}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "monospace" }} />
              <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} />
              <Tooltip contentStyle={{ fontSize: 12, fontFamily: "monospace" }} />
              <ReferenceLine y={0} stroke="#10b981" strokeDasharray="3 3" />
              <Line type="monotone" dataKey="drift" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
          <div className="text-[10px] text-muted-foreground mt-1">Green dashed = zero-drift target · red line = actual drift per period-close</div>
        </Card>
      )}
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>SKU</th><th>Item</th><th className="text-right">Qty</th><th className="text-right">Master ₹</th><th className="text-right">Movements ₹</th><th className="text-right">Drift ₹</th></tr></thead>
          <tbody>
            {(d.rows || []).length === 0 && <tr><td colSpan="6" className="text-center py-10 text-muted-foreground">No drift · books tally.</td></tr>}
            {(d.rows || []).map((r) => (
              <tr key={r.item_id} className={Math.abs(r.drift) > 0.5 ? "bg-red-500/5" : ""}>
                <td className="font-mono text-xs">{r.sku}</td>
                <td>{r.name}</td>
                <td className="text-right tabular-nums">{r.qty}</td>
                <td className="text-right tabular-nums">{fmtINR(r.book_value_master)}</td>
                <td className="text-right tabular-nums">{fmtINR(r.book_value_movements)}</td>
                <td className={`text-right tabular-nums font-medium ${r.drift > 0 ? "text-emerald-700" : r.drift < 0 ? "text-red-700" : ""}`}>{fmtINR(r.drift)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
